import { Component, computed, input } from '@angular/core';

export type UiSkeletonVariant = 'text' | 'block' | 'circle' | 'card' | 'stat' | 'panel' | 'brand';
export type UiSkeletonSize = 'sm' | 'md' | 'lg';

const TEXT_WIDTHS = [100, 96, 88, 76, 64, 52];
const CIRCLE_SIZES: Record<UiSkeletonSize, string> = {
  sm: '2rem',
  md: '2.75rem',
  lg: '3rem',
};
const PANEL_MIN_HEIGHT: Record<UiSkeletonSize, string> = {
  sm: '7.5rem',
  md: '16rem',
  lg: '18rem',
};

@Component({
  selector: 'ui-skeleton',
  standalone: true,
  template: `
    @switch (variant()) {
      @case ('circle') {
        <span
          class="ui-skeleton__circle veitros-skeleton-shimmer"
          [class.ui-skeleton__shimmer--static]="!animated()"
          [style.width]="circleSize()"
          [style.height]="circleSize()"
          aria-hidden="true"
        ></span>
      }

      @case ('block') {
        @for (_ of repeatArray(); track $index) {
          <span
            class="ui-skeleton__block veitros-skeleton-shimmer"
            [class.ui-skeleton__shimmer--static]="!animated()"
            [style.width]="resolvedWidth()"
            [style.height]="resolvedHeight()"
            [style.border-radius]="resolvedRadius()"
            aria-hidden="true"
          ></span>
        }
      }

      @case ('card') {
        <div class="ui-skeleton__card" aria-hidden="true">
          <span
            class="ui-skeleton__card-title veitros-skeleton-shimmer"
            [class.ui-skeleton__shimmer--static]="!animated()"
          ></span>
          <div class="ui-skeleton__card-body">
            @for (_ of rowArray(); track $index) {
              <span
                class="ui-skeleton__line veitros-skeleton-shimmer"
                [class.ui-skeleton__shimmer--static]="!animated()"
                [style.width.%]="lineWidth($index)"
              ></span>
            }
          </div>
        </div>
      }

      @case ('stat') {
        <div class="ui-skeleton__stat" aria-hidden="true">
          <span
            class="ui-skeleton__stat-icon veitros-skeleton-shimmer"
            [class.ui-skeleton__shimmer--static]="!animated()"
          ></span>
          <div class="ui-skeleton__stat-text">
            <span
              class="ui-skeleton__stat-label veitros-skeleton-shimmer"
              [class.ui-skeleton__shimmer--static]="!animated()"
            ></span>
            <span
              class="ui-skeleton__stat-value veitros-skeleton-shimmer"
              [class.ui-skeleton__shimmer--static]="!animated()"
            ></span>
          </div>
        </div>
      }

      @case ('panel') {
        @for (_ of repeatArray(); track $index) {
          <span
            class="ui-skeleton__panel veitros-skeleton-shimmer"
            [class.ui-skeleton__shimmer--static]="!animated()"
            [style.min-height]="panelHeight()"
            aria-hidden="true"
          ></span>
        }
      }

      @case ('brand') {
        <div class="ui-skeleton__brand" aria-hidden="true">
          <span
            class="ui-skeleton__brand-logo veitros-skeleton-shimmer"
            [class.ui-skeleton__shimmer--static]="!animated()"
          ></span>
          <div class="ui-skeleton__brand-text">
            <span
              class="ui-skeleton__brand-line ui-skeleton__brand-line--lg veitros-skeleton-shimmer"
              [class.ui-skeleton__shimmer--static]="!animated()"
            ></span>
            <span
              class="ui-skeleton__brand-line veitros-skeleton-shimmer"
              [class.ui-skeleton__shimmer--static]="!animated()"
            ></span>
          </div>
        </div>
      }

      @default {
        <div class="ui-skeleton__text" aria-hidden="true">
          @for (_ of rowArray(); track $index) {
            <span
              class="ui-skeleton__line veitros-skeleton-shimmer"
              [class.ui-skeleton__shimmer--static]="!animated()"
              [style.width.%]="lineWidth($index)"
            ></span>
          }
        </div>
      }
    }
  `,
  styles: [`
    :host {
      display: block;
      min-width: 0;
    }

    :host([data-inline]) {
      display: inline-block;
    }

    .ui-skeleton__shimmer--static {
      animation: none !important;
      background: var(--skeleton-base, var(--surface-muted)) !important;
    }

    .ui-skeleton__text,
    .ui-skeleton__card-body {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .ui-skeleton__line {
      display: block;
      height: 1rem;
      border-radius: 0.25rem;
    }

    .ui-skeleton__block {
      display: block;
      width: 100%;
      min-height: 1rem;
      border-radius: 0.25rem;
    }

    .ui-skeleton__circle {
      display: inline-block;
      border-radius: 999px;
      flex-shrink: 0;
    }

    .ui-skeleton__card {
      display: flex;
      flex-direction: column;
      gap: 0.875rem;
      padding: 1rem;
      border-radius: 0.75rem;
      border: 1px solid var(--border-color, #e8e8e8);
      background: var(--surface-bg, #fff);
    }

    .ui-skeleton__card-title {
      display: block;
      width: 42%;
      height: 1.125rem;
      border-radius: 0.375rem;
    }

    .ui-skeleton__stat {
      display: flex;
      align-items: center;
      gap: 0.875rem;
      padding: 1rem 1.125rem;
      border-radius: 0.75rem;
      border: 1px solid var(--border-color, #e8e8e8);
      background: var(--surface-bg, #fff);
    }

    .ui-skeleton__stat-icon {
      width: 2.75rem;
      height: 2.75rem;
      border-radius: 0.625rem;
      flex-shrink: 0;
    }

    .ui-skeleton__stat-text {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      flex: 1;
      min-width: 0;
    }

    .ui-skeleton__stat-label {
      display: block;
      width: 56%;
      height: 0.75rem;
      border-radius: 999px;
    }

    .ui-skeleton__stat-value {
      display: block;
      width: 38%;
      height: 1.25rem;
      border-radius: 0.375rem;
    }

    .ui-skeleton__panel {
      display: block;
      width: 100%;
      border-radius: 0.75rem;
      border: 1px solid var(--border-color, #e8e8e8);
    }

    .ui-skeleton__brand {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.625rem 0.75rem;
      border-radius: 0.75rem;
      border: 1px solid var(--border-color, #e8e8e8);
      background: var(--surface-muted, #f8fafc);
    }

    .ui-skeleton__brand-logo {
      width: 3rem;
      height: 3rem;
      border-radius: 0.625rem;
      flex-shrink: 0;
    }

    .ui-skeleton__brand-text {
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
      flex: 1;
      min-width: 0;
    }

    .ui-skeleton__brand-line {
      display: block;
      height: 0.5rem;
      border-radius: 999px;
      width: 48%;
    }

    .ui-skeleton__brand-line--lg {
      height: 0.625rem;
      width: 72%;
    }
  `],
})
export class UiSkeletonComponent {
  /** text | block | circle | card | stat | panel | brand */
  variant = input<UiSkeletonVariant>('text');
  /** Linhas no modo text/card. */
  rows = input(3);
  /** Largura das linhas no modo text (legado). */
  widthPercent = input(100);
  /** Largura do bloco (ex.: '100%', '8rem', 120). */
  width = input<string | number | null>(null);
  /** Altura do bloco (ex.: '1rem', '6.5rem', 24). */
  height = input<string | number | null>(null);
  /** Tamanho para circle/stat/panel. */
  size = input<UiSkeletonSize>('md');
  /** Repetições para block/panel. */
  count = input(1);
  /** Animação shimmer. */
  animated = input(true);
  /** Raio customizado para block. */
  borderRadius = input<string | null>(null);

  readonly circleSize = computed(() => CIRCLE_SIZES[this.size()]);
  readonly panelHeight = computed(() => PANEL_MIN_HEIGHT[this.size()]);

  rowArray(): number[] {
    return Array.from({ length: Math.max(0, this.rows()) });
  }

  repeatArray(): number[] {
    return Array.from({ length: Math.max(1, this.count()) });
  }

  lineWidth(index: number): number {
    if (this.widthPercent() !== 100) {
      return this.widthPercent();
    }
    return TEXT_WIDTHS[index % TEXT_WIDTHS.length];
  }

  resolvedWidth(): string {
    const value = this.width();
    if (value === null || value === undefined || value === '') {
      return '100%';
    }
    return typeof value === 'number' ? `${value}px` : value;
  }

  resolvedHeight(): string {
    const value = this.height();
    if (value === null || value === undefined || value === '') {
      return '1rem';
    }
    return typeof value === 'number' ? `${value}px` : value;
  }

  resolvedRadius(): string {
    return this.borderRadius() ?? '0.25rem';
  }
}
